import * as EmotionCSS from '@emotion/css';
import _createInstance from '@emotion/css/create-instance';
import { serializeStyles } from '@emotion/serialize';
import { generateUniqueId } from './uniqueId';
let _instance;
export const createStylesCache = {};
/**
 * Wrap all unwrapped CSS Variables. For example, `{padding: '--foo'}` will be replaced with
 * `{padding: 'var(--foo)'}`. It also works on variables in the middle of the property.
 */
function maybeWrapCSSVariables(input) {
    // matches an string starting with `--` that isn't already wrapped in a `var()`. It tries to match
    // any character that isn't a valid separator in CSS
    return input.replace(/([a-z]*[ (]*)(--[^\s;,'})]+)/gi, (match, prefix, variable) => {
        if (prefix === 'var(') {
            return match;
        }
        return `${prefix}var(${variable})`;
    });
}
function convertProperty(value) {
    // Handle the case where the value is a variable without the `var()` wrapping function. It happens
    // enough that it makes sense to automatically wrap.
    if (typeof value === 'string') {
        return maybeWrapCSSVariables(value);
    }
    return value;
}
/**
 * Walks through all the properties and values of a style and converts properties and/or values that
 * need special processing. An example might be using a CSS variable without a `var()` wrapping.
 */
function convertAllProperties(obj) {
    if (typeof obj === 'object') {
        const converted = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                converted[key] = convertAllProperties(obj[key]);
            }
        }
        return converted;
    }
    return convertProperty(obj);
}
/**
 * Take a CSS Variable name and return a variable property
 *
 * ```ts
 * const myVars = createVars('color')
 *
 * const myStyles = createStyles({
 *   color: cssVar(myVars.color) // color: 'var(--color-{hash})'
 * })
 * ```
 *
 * It can also support an optional fallback. Fallbacks should only be used if it is reasonable to
 * expect a CSS variable isn't defined.
 * ```ts
 * const myStyles = createStyles({
 *   color: cssVar(myVars.color, 'red') // color: 'var(--color-{hash}, red)'
 * })
 * ```
 *
 * If the project is set up for parsing with fallback files, a fallback will automatically be filled
 * during the parsing phase. This is helpful for cases when CSS variables are expected, but not set
 * in the environment.
 */
export function cssVar(input, fallback) {
    return fallback
        ? `var(${input}, ${fallback.startsWith('--') ? `var(${fallback})` : fallback})`
        : `var(${input})`;
}
export function createVars(...args) {
    const arg0 = args[0];
    if (typeof arg0 === 'object' && !arg0.id) {
        // this is a defaulted var
        return createDefaultedVars(arg0, args[1]);
    }
    const id = args[0].id || generateUniqueId();
    // eslint-disable-next-line no-param-reassign
    args = args[0].args || args;
    const result = (input) => {
        const vars = {};
        args.forEach(key => {
            if (input[key]) {
                // @ts-ignore TS complains about `key` not in object `{}`
                vars[result[key]] = convertProperty(input[key]);
            }
        });
        return vars;
    };
    args.forEach(key => {
        // @ts-ignore
        result[key] = `--${key}-${id}`;
    }, {});
    return result;
}
export function createDefaultedVars(input, id) {
    const localId = id || generateUniqueId();
    const result = input => {
        const vars = {};
        // eslint-disable-next-line guard-for-in
        for (const key in input) {
            if (typeof input[key] === 'string') {
                if (result[key]) {
                    // Don't add an undefined key
                    vars[result[key]] = convertProperty(input[key]);
                }
            }
            if (typeof input[key] === 'object') {
                for (const subKey in input[key]) {
                    if (typeof input[key][subKey] === 'string') {
                        // Don't add an undefined key
                        if (result[key][subKey]) {
                            vars[result[key][subKey]] = convertProperty(input[key][subKey]);
                        }
                    }
                }
            }
        }
        return vars;
    };
    result.$$defaults = {};
    // eslint-disable-next-line guard-for-in
    for (const key in input) {
        if (typeof input[key] === 'string') {
            const cssVarName = `--${key}-${localId}`;
            result[key] = cssVarName;
            result.$$defaults[cssVarName] = input[key];
        }
        if (typeof input[key] === 'object') {
            result[key] = {};
            for (const subKey in input[key]) {
                if (typeof input[key][subKey] === 'string') {
                    const cssVarName = `--${key}-${subKey}-${localId}`;
                    result[key][subKey] = cssVarName;
                    result.$$defaults[cssVarName] = input[key][subKey];
                }
            }
        }
    }
    return result;
}
/**
 * Creates a modifier function that takes in a modifier config and will return a CSS class name that
 * matches the result. Modifiers can be thought as `if` or `switch` statements when conditionally
 * changing the styles of a component based on props. This function can be thought of as a helper
 * function that makes it easier to work with modifiers. Without it, you would have to implement
 * if/switch/ternary for each option.
 *
 * ```tsx
 * const myModifiers = createModifiers({
 *   // a modifier called 'size'
 *   size: {
 *     small: createStyles({ fontSize: 12 }),
 *     medium: createStyles({ fontSize: 14 })
 *   }
 * })
 *
 * // with the modifier function
 * myModifiers({ size: 'medium' }) // returns the medium class name
 *
 * // manually without the function
 * size === 'small' ? myModifiers.size.small : size === 'medium' ? myModifiers.size.medium : ''
 * ```
 */
export function createModifiers(input) {
    const modifierFn = (modifiers) => {
        return combineClassNames(Object.keys(input)
            .filter(key => input[key][modifiers[key]])
            .map(key => input[key][modifiers[key]]));
    };
    Object.keys(input).forEach(key => {
        // @ts-ignore TypeScript makes it a pain to deal with index types
        modifierFn[key] = input[key];
    });
    return modifierFn;
}
/**
 * Creates a compound modifier function that takes in a modifier config and a compound modifier
 * config and returns a space-delimited list of CSS class names to apply to an element. This
 * function is similar to the {@link createModifiers} function.
 *
 * ```tsx
 * const myModifiers = createModifiers({
 *   size: {large: 'large'},
 *   position: {start: 'start'}
 * })
 *
 * const myCompound = createCompoundModifiers(myModifiers, [
 *   {
 *     modifiers: {
 *       size: 'large',
 *       position: 'start'
 *     },
 *     styles: 'large-start'
 *   }
 * ])
 *
 * myCompound({size: 'large', position: 'start'}) // 'large-start'
 * myCompound({size: 'large'}) // ''
 * ```
 */
export function createCompoundModifiers(
/**
 * Modifiers created via `createModifiers`. The type will be extracted and applied to your
 * compound modifiers.
 */
modifiers, compound) {
    return modifiers => {
        return combineClassNames(compound.map(compoundModifier => {
            const match = Object.keys(compoundModifier.modifiers).reduce((result, compoundModifierKey) => {
                return (result &&
                    compoundModifier.modifiers[compoundModifierKey] === modifiers[compoundModifierKey]);
            }, true);
            if (match) {
                return compoundModifier.styles;
            }
            return '';
        }));
    };
}
function isCsPropsReturn(input) {
    return input.hasOwnProperty('style') || input.hasOwnProperty('className');
}
/**
 * A function that takes in a single input, or an array. The type of the input is either:
 *
 * - `string` - it represents a CSS class name
 * - `undefined` - there is no value. This is provided for convenience for developers to not have to
 *   filter out undefined
 * - `{--{varName}-{hash}: {value}}` - a `Map` of CSS variable to values
 * - `{style: ..., className: ...}` an object already returned by another `csToProps` function call
 *   (for nesting)
 * - An array containing any of the above. This will recurse over each entry to produce a single,
 *   reduced `{style: ..., className: ...}` object
 * @param input
 * @returns
 */
export function csToProps(input) {
    const instance = getInstance();
    // input is a string, so it must only be a class name
    if (typeof input === 'string') {
        return { className: input };
    }
    // input isn't defined, so we'll return an empty object
    if (!input) {
        return {};
    }
    // A nested `csToProps`, we'll simply return with no modification
    if (isCsPropsReturn(input)) {
        return input;
    }
    // At this point, `input` is either an array or a `Record<T, string>`. If it isn't an array, it
    // must be a style object for setting CSS variables. So set the `style` attribute
    if (!Array.isArray(input)) {
        // The input is an object. We'll separate variables from static styles. If static styles are
        // detected here, a dynamic mode should be detected and done for development. The transform
        // should take care of extracting static styles out of a render function and this function
        // should not be run.
        let hasStaticStyles = false;
        const staticStyles = {};
        const cssVars = {};
        for (const key in input) {
            if (key.startsWith('--')) {
                cssVars[key] = input[key];
            }
            else {
                staticStyles[key] = input[key];
                hasStaticStyles = true;
            }
        }
        if (hasStaticStyles) {
            const className = instance.css(staticStyles);
            return { style: cssVars, className };
        }
        return { style: input };
    }
    // An array. it is the only thing left. We iterate and recurse over the array to produce a single
    // object with `style` and `className` attributes to spread on an element
    return input.map(csToProps).reduce((result, val) => {
        return {
            className: combineClassNames([result.className, val.className]),
            style: { ...result.style, ...val.style },
        };
    }, {});
}
/**
 * Creates CSS styles based on object-style input. It has a side-effect of adding CSS to the page
 * and will return a space-delimitated string of CSS class names meant to be added to an element.
 *
 * It can take a number of inputs of various types. The simplest is object-styles.
 *
 * ```ts
 * const myStyles = createStyles({
 *   backgroundColor: 'red'
 * })
 * ```
 *
 * The `createStyles` function is curried into 2 parts. The first function could be done at build
 * time. The returned function combines CSS class names and will remain as a small runtime.
 *
 * > **Note:** The order of calling `createStyles` is important. Each call will make a single CSS
 * > class selector and will be injected into the document's
 * > [StyleSheetList](https://developer.mozilla.org/en-US/docs/Web/API/StyleSheetList). Style
 * > properties will be merge by the rules of [CSS
 * > specificity](https://developer.mozilla.org/en-US/docs/Web/CSS/Specificity). If two selectors
 * > have the same specificity, the last defined wins. Always make sure that the properties you want
 * > to win are last in your file.
 */
export function createStyles(...args) {
    const instance = getInstance();
    return args
        .map(input => {
        if (typeof input === 'string') {
            return input;
        }
        // If we were called with a {name, styles} object, it must be optimized. We'll shortcut here
        if (typeof input === 'object' && input.name) {
            createStylesCache[`${instance.cache.key}-${input.name}`] = true;
            return instance.css(input);
        }
        const convertedStyles = convertAllProperties(input);
        // We want to call `serializeStyles` directly and ignore the hash generated so we can have
        // more predictable style merging. If 2 different files define the same style properties, the
        // hash will be the same and using the default hash functionality of Emotion will mean the
        // merge order will be unpredictable. For example, `BaseButton` and `TertiaryButton` define
        // different modifiers for padding. The BaseButton's `extraSmall` modifier contains the same
        // styling as TertiaryButton's `large` modifier. Emotion would hash these two declarations as
        // the same hash and only inject the one from `BaseButton`. If `TertiaryButton` defines
        // `padding` in its base styles, and uses the large size modifier, the base padding will
        // override the `TertiaryButton` `large` size modifier because `BaseButton.small` modifier was
        // injected into the document's style sheets before `TertiaryButton.base` styles. This is due
        // to CSS specificity. If everything has the same specificity, last defined wins. More info:
        // https://codesandbox.io/s/stupefied-bartik-9c2jtd?file=/src/App.tsx
        const { styles } = serializeStyles([convertedStyles]);
        const name = generateUniqueId();
        createStylesCache[`${instance.cache.key}-${name}`] = true;
        return instance.css({ name, styles });
    })
        .join(' ');
}
/**
 * This function handles the `cs` prop for you, as well as local styles you want to define. It will
 * force style merging with Emotion's runtime APIs, including [styled
 * components](https://emotion.sh/docs/styled) and the [css prop](https://emotion.sh/docs/css-prop).
 *
 * Runtime style merging works by forcing Emotion's styling merging if use of runtime APIs have been
 * detected. If only `createStyles` were used to style a component, the faster non-runtime styling
 * will be used.
 *
 * You can use `handleCsProp` if you wish to use {@link createStyles} on your own components and want
 * your components to be compatible with Emotion's runtime styling APIs.
 *
 * ```tsx
 * import {createStyles, handleCsProp, CSProps} from '@workday/canvas-kit-styling';
 *
 * interface MyComponentProps extends CSProps {
 *   // other props
 * }
 *
 * const myStyles = createStyles({
 *   background: 'green',
 *   height: 40,
 *   width: 40
 * })
 *
 * const MyComponent = ({children, ...elemProps}: MyComponentProps) => {
 *   return (
 *     <div
 *       {...handleCsProp(elemProps, [myStyles])}
 *     >
 *       {children}
 *     </div>
 *   )
 * }
 *
 * const StyledMyComponent(MyComponent)({
 *   background: 'red'
 * })
 *
 * const myOverridingStyles = createStyles({
 *   background: 'blue'
 * })
 *
 * // now everything works. Without `handleCsProp`, the last component would be a red box
 * export default () => (
 *   <>
 *     <MyComponent>Green box</MyComponent>
 *     <StyledMyComponent>Red box</StyledMyComponent>
 *     <StyledMyComponent cs={myOverridingStyles}>Blue box</StyledMyComponent>
 *   </>
 * )
 * ```
 */
export function handleCsProp(
/**
 * All the props to be spread onto an element. The `cs` prop will be removed an reduced to
 * `className` and `style` props which should be safe on every element.
 */
elemProps, 
/**
 * Optional local style created by `createStyles`. Using this parameter, you can style your
 * element while supporting proper style merging order.
 */
localCs) {
    const { cs, style, className, ...restProps } = elemProps;
    const instance = getInstance();
    // We're seeing style merging issues when using createStyles or createStencil. It only happens when
    // every style override of the element uses these utilities and @emotion/react or @emotion/styled is not used on the same element.
    // These utilities rely on module execution order and we're having a few reports where modules are possibly executing out of order.
    // In order to allow everyone to use createStyles and createStencil without worrying about style merge issues, we're going
    // to enable compat mode all the time. We'll look into possible out-of-order execution issues in the future and plan to re-enable
    // full static mode (for better performance) once we know why this is happening and have a proper workaround.
    let shouldRuntimeMergeStyles = true;
    // The order is intentional. The `className` should go first, then the `cs` prop. If we don't do
    // runtime merging, this order doesn't actually matter because the browser doesn't care the order
    // of classes on the element's
    // [classList](https://developer.mozilla.org/en-US/docs/Web/API/Element/classList). But Emotion
    // _does_ care because it uses the `classList` order to determine style merging. Therefore we
    // should always be careful to order class names knowing we need to support runtime merging if
    // runtime styles are detected.
    const returnProps = csToProps([localCs, className, cs]);
    // The following is basically what Emotion does internally to merge styles from different
    // components into a single className. We will do this here for backwards compatibility with
    // runtime style merging. `createStyles` and `@emotion/css` works by creating styles and injecting
    // them immediately into the document. `@emotion/react` and `@emotion/styled` work by injecting
    // styles during the React render cycle. The following links are to source code of
    // `@emotion/styled` and `@emotion/react` respectively that merge styles in a similar manner.
    // - https://github.com/emotion-js/emotion/blob/f3b268f7c52103979402da919c9c0dd3f9e0e189/packages/styled/src/base.js#L120C51-L138
    // - https://github.com/emotion-js/emotion/blob/f3b268f7c52103979402da919c9c0dd3f9e0e189/packages/react/src/emotion-element.js#L102-L116
    const returnClassNames = (returnProps.className || '').split(' ');
    returnClassNames.forEach(name => {
        // Here we detect if a className is associated with Emotion's cache. Styles created via
        // `createStyles` do not need special runtime merge treatment, but `@emotion/react` and
        // `@emotion/styled` do. The `createStylesCache` tracks all styles injected into the cache via
        // `createStyles`, so those are filtered out.
        if (!createStylesCache[name] && instance.cache.registered[name]) {
            shouldRuntimeMergeStyles = true;
        }
    });
    returnProps.style = { ...returnProps.style, ...style };
    // If runtime styles are detected, we need to do extra work to ensure styles merge according to
    // the rules of Emotion's runtime.
    if (shouldRuntimeMergeStyles) {
        returnProps.className = getInstance().cx(returnClassNames);
    }
    return {
        ...returnProps,
        ...restProps,
    };
}
function onlyDefined(input) {
    return !!input;
}
function onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
}
/**
 * Combines an array of space-delimited CSS class names into a single string of space-delimited
 * class names. Duplicate class names are removed.
 */
function combineClassNames(input) {
    return input
        .join(' ')
        .split(' ')
        .filter((s, i, a) => onlyDefined(s) && onlyUnique(s, i, a))
        .join(' ');
}
/**
 * This function is used in conjunction with Stencils to get a selector to match the current element
 * and a parent modifier. The selector will match a parent element with a modifier applied and the current
 * element. It should be used on the `base` config of a Stencil.
 *
 * ```ts
 * const childStencil = createStencil({
 *   base: {
 *     // base styles
 *     [parentModifier(parentStencil.modifiers.size.large)]: {
 *       // maybe adjust padding of this element
 *     }
 *   }
 * })
 * ```
 */
export function parentModifier(value) {
    return `.${value.replace('css-', 'm')} :where(&)`;
}
/**
 * Creates a reuseable Stencil for styling elements. It takes vars, base styles, modifiers, and
 * compound modifiers.
 */
export function createStencil(config, id) {
    const { vars, base, modifiers, compound, defaultModifiers } = config;
    const composes = config.extends;
    const _vars = createDefaultedVars(vars || {}, id); // The return type is conditional and TypeScript doesn't like that here
    // combine the vars keys together
    Object.keys((composes === null || composes === void 0 ? void 0 : composes.vars) || {}).forEach(key => {
        if (key !== '$$defaults') {
            // @ts-ignore
            _vars[key] = composes === null || composes === void 0 ? void 0 : composes.vars[key];
        }
    });
    const _base = createStyles({
        ..._vars.$$defaults,
        boxSizing: 'border-box',
        ...(typeof base === 'function' ? base(_vars) : base),
    });
    const composesModifier = (composes === null || composes === void 0 ? void 0 : composes.modifiers) || (() => '');
    const _modifiers = modifiers
        ? createModifiers(Object.keys(modifiers).reduce((result, key) => {
            // @ts-ignore
            result[key] = Object.keys(modifiers[key]).reduce((result, modifierKey) => {
                const modifier = modifiers[key][modifierKey];
                // @ts-ignore
                result[modifierKey] = createStyles(typeof modifier === 'function' ? modifier(_vars) : modifier);
                return result;
            }, {});
            return result;
        }, {}))
        : () => '';
    Object.keys(composesModifier).forEach(modifier => {
        // @ts-ignore
        Object.keys(composesModifier[modifier]).forEach(value => {
            // @ts-ignore
            if (!_modifiers[modifier]) {
                // @ts-ignore
                _modifiers[modifier] = {};
            }
            // @ts-ignore
            _modifiers[modifier][value] = combineClassNames([
                // @ts-ignore
                composesModifier[modifier][value],
                // @ts-ignore
                _modifiers[modifier][value],
            ]);
        });
    });
    const _compound = compound
        ? createCompoundModifiers(
        // @ts-ignore modifiers are different in a stencil than in createModifiers. The difference is createModifiers expects each value to be a `string` and stencils expects `StyleProps`. Casting is gnarly, so we'll ignore instead
        modifiers, compound.map(compoundModifier => {
            return {
                modifiers: compoundModifier.modifiers,
                styles: createStyles(compoundModifier.styles),
            };
        }))
        : () => '';
    const stencil = ((input) => {
        const inputModifiers = { ...composes === null || composes === void 0 ? void 0 : composes.defaultModifiers, ...defaultModifiers };
        // Only override defaults if a value is defined
        for (const key in input) {
            if (input[key] !== undefined) {
                // @ts-ignore
                inputModifiers[key] = input[key];
            }
        }
        const composesReturn = composes === null || composes === void 0 ? void 0 : composes(inputModifiers);
        const modifierClasses = _modifiers(inputModifiers);
        return {
            className: combineClassNames([
                composesReturn === null || composesReturn === void 0 ? void 0 : composesReturn.className,
                _base,
                modifierClasses,
                // For compat mode, we need to add inert class names of modifiers where the `css-` prefix is
                // removed. For example, `css-base css-mod-1` will become `css-base css-mod-1 mod-1`. The
                // modifier class without the prefix will be ignored by the Emotion CSS `cx` function and
                // will remain for the `parentModifier` function to still select on. We decided to add these
                // inert class names instead of adding `data-m-*` attributes because the output DOM looks
                // much cleaner and it saves bytes on the bundled output.
                modifierClasses.replace(/css-/g, 'm'),
                compound ? _compound(inputModifiers) : '',
            ]),
            style: { ...composesReturn === null || composesReturn === void 0 ? void 0 : composesReturn.style, ..._vars(input || {}) },
        };
    });
    stencil.vars = _vars;
    stencil.base = combineClassNames([composes === null || composes === void 0 ? void 0 : composes.base, _base]);
    stencil.modifiers = _modifiers; // The return type is conditional and TypeScript doesn't like that here
    stencil.defaultModifiers = { ...composes === null || composes === void 0 ? void 0 : composes.defaultModifiers, ...defaultModifiers };
    stencil.__extends = composes; // The return type is conditional and TypeScript doesn't like that here
    return stencil;
}
/**
 * Gets the current Emotion CSS instance, falling back to the one from `@emotion/css` if one wasn't
 * already created. This allows a custom cache to be created as an opt-in
 */
export function getInstance() {
    if (!_instance) {
        _instance = EmotionCSS;
    }
    return _instance;
}
/**
 * Creates a custom instance of Emotion CSS. If this function is never called, the instance will be
 * what gets imported from `@emotion/css`. This function must be called before any Canvas Kit
 * component is imported or before any other `@workday/canvas-kit-styling` function is called. All
 * the style utility functions need an instance and will automatically create one if one isn't
 * already created.
 *
 * The style utilities inject styles as soon as they are called which means an instance needs to be
 * created before any Canvas Kit components are even imported. Your application bootstrap must
 * import a file that imports `@workday/canvas-kit-styling` and calls `createInstance` _before_ any
 * other Canvas Kit components are imported.
 */
export const createInstance = options => {
    if (!_instance) {
        _instance = _createInstance({ key: 'css', ...options });
    }
    else {
        // @ts-ignore
        if (process && process.env.NODE_ENV === 'development') {
            console.warn('An instance has already been created. `createInstance` cannot be called after styles have already been created. Canvas Kit styles are created immediately, so this function must be called before any Canvas Kit components are even imported.');
        }
    }
    return _instance;
};
/**
 * Returns the cache used by all style utilities
 */
export function getCache() {
    return getInstance().cache;
}
/**
 * Create static keyframes. Use as a drop-in replacement to `keyframes` found in `@emotion/css` or
 * `@emotion/react`
 */
export function keyframes(...args) {
    return getInstance().keyframes(...args);
}
/**
 * Allows injecting of global styles.
 */
export const injectGlobal = (...args) => getInstance().injectGlobal(...args);
